import {
  ArrowLeftIcon,
  CheckIcon,
  ImageIcon,
  Package,
  Plus,
  SearchIcon,
} from "@/components/icons";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  ScrollArea,
  Separator,
  Skeleton,
  toast,
  useDebounceCallback,
} from "@/components/kit";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  useAssembleBundle,
  useBundleAssemblableQty,
} from "@/modules/inventory/presentation/api/queries";
import type { ProductPickerItemDTO } from "@/modules/products/domain/types";
import { formatQtyWithUnit } from "@/modules/products/domain/unit-conversion";
import { formatProductCategories } from "@/modules/products/presentation/lib/categories";
import { formatLak, formatLakRange } from "@/shared/lib/currency";
import { AppImage } from "@/shared/ui/AppImage";
import {
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useProductPickerQuery } from "../api/queries";
import { filterPickerProducts } from "../lib/filter-picker-products";

export type PickedVariant = {
  variantId: string;
  productName: string;
  variantName: string;
  unitPrice: string;
  imageKey: string | null;
  stockQuantity: number;
  stockUnitLabel: string;
  receiveUnitLabel: string | null;
  receiveConversionFactor: string;
  unitsPerSale: number;
};

type ProductPickerModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (variant: PickedVariant) => void;
  allowZeroStock?: boolean;
  purpose?: "order" | "receive";
  subtitle?: string;
  /** ไม่นับจำนวนใน draft order นี้ (เวลาแก้ไขออเดอร์ draft) */
  excludeOrderId?: string;
  /** จำนวนในตะกร้าปัจจุบัน (variantId → qty) — หักจาก available เพิ่ม */
  localReservedByVariant?: Record<string, number>;
  /** Phase 15 — ຊ່ອນ variant ເຊັດ (ໃຊ້ເລືອກສ່ວນປະກອບ ad-hoc kit) */
  excludeBundles?: boolean;
  /** Phase 25 — ໄປສ້າງສິນຄ້າໃໝ່ (ກັບມາ prefill ໃບສັ່ງນຳເຂົ້າ) */
  onCreateProduct?: () => void;
  createProductLabel?: string;
  /** Phase 25 — ສ້າງ variant ໃໝ່ໃນສິນຄ້າທີ່ເລືອກແລ້ວ */
  onCreateVariant?: (product: ProductPickerItemDTO) => void;
  createVariantLabel?: string;
};

type PickerStep = "products" | "variants";
type PickerVariant = ProductPickerItemDTO["variants"][number];

function variantAvailableQty(variant: PickerVariant): number {
  return variant.availableQuantity ?? variant.stockQuantity;
}

function applyLocalReservedToProducts(
  products: ProductPickerItemDTO[],
  localReservedByVariant?: Record<string, number>,
): ProductPickerItemDTO[] {
  if (!localReservedByVariant) return products;

  return products.map((product) => ({
    ...product,
    variants: product.variants.map((variant) => {
      const localReserved = localReservedByVariant[variant.id] ?? 0;
      if (localReserved <= 0) return variant;

      const baseAvailable = variantAvailableQty(variant);
      const availableQuantity = Math.max(0, baseAvailable - localReserved);
      const draftReservedQuantity = Math.max(
        0,
        variant.stockQuantity - availableQuantity,
      );

      return {
        ...variant,
        availableQuantity,
        draftReservedQuantity,
      };
    }),
  }));
}

function buildPickedVariant(
  variant: ProductPickerItemDTO["variants"][number],
  product: ProductPickerItemDTO,
): PickedVariant {
  return {
    variantId: variant.id,
    productName: product.name,
    variantName: variant.name,
    unitPrice: variant.price,
    imageKey: variant.imageKey ?? product.imageKey,
    stockQuantity: variantAvailableQty(variant),
    stockUnitLabel: variant.stockUnitLabel,
    receiveUnitLabel: variant.receiveUnitLabel,
    receiveConversionFactor: variant.receiveConversionFactor,
    unitsPerSale: Number(variant.unitsPerSale) || 1,
  };
}

function productPriceRange(product: ProductPickerItemDTO) {
  const prices = product.variants.map((v) => Number(v.price));
  if (prices.length === 0) return null;
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return { min: String(min), max: String(max) };
}

function productStockSummary(
  product: ProductPickerItemDTO,
  allowZeroStock: boolean,
) {
  const total = product.variants.length;
  const inStock = product.variants.filter(
    (v) => allowZeroStock || variantAvailableQty(v) > 0,
  ).length;
  return { total, inStock, allOut: total > 0 && inStock === 0 };
}

function productHasBundleVariant(product: ProductPickerItemDTO) {
  return product.variants.some((v) => v.variantType === "bundle");
}

function matchesSearch(text: string, query: string) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return text.toLowerCase().includes(q);
}

function PickerSearch({
  value,
  onChange,
  placeholder,
  autoFocus = false,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  autoFocus?: boolean;
}) {
  return (
    <InputGroup className="min-h-11">
      <InputGroupAddon align="inline-start">
        <SearchIcon />
      </InputGroupAddon>
      <InputGroupInput
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="min-h-11"
      />
    </InputGroup>
  );
}

function ProductSelectCard({
  product,
  allowZeroStock,
  enableAssemble,
  onSelect,
}: {
  product: ProductPickerItemDTO;
  allowZeroStock: boolean;
  enableAssemble: boolean;
  onSelect: () => void;
}) {
  const price = productPriceRange(product);
  const stock = productStockSummary(product, allowZeroStock);
  const hasBundle = productHasBundleVariant(product);
  /** ເຊັດສະຕັອກ 0 ຍັງເປີດໄດ້ — ເພື່ອກົດປະກອບໃນໜ້າຕົວເລືອກ */
  const openForAssemble = enableAssemble && hasBundle && stock.allOut;
  const disabled =
    product.variants.length === 0 || (stock.allOut && !openForAssemble);

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "group flex h-full flex-col overflow-hidden rounded-xl border text-left transition-colors",
        disabled
          ? "cursor-not-allowed border-border/60 bg-muted/30 opacity-60"
          : "border-secondary/25 bg-card hover:border-secondary/50 hover:bg-secondary/5 active:bg-secondary/10",
      )}
    >
      <div className="relative aspect-square w-full overflow-hidden bg-muted/40">
        {product.imageKey ? (
          <AppImage
            src={product.imageKey}
            alt={product.name}
            className="size-full transition-transform duration-300 group-hover:scale-[1.03]"
            fit="cover"
          />
        ) : (
          <div className="flex size-full items-center justify-center text-muted-foreground">
            <ImageIcon className="size-8 opacity-40" />
          </div>
        )}
        {openForAssemble ? (
          <Badge
            variant="outline"
            className="absolute top-2 right-2 border-amber-600/30 bg-amber-500/95 text-[10px] text-amber-950 shadow-sm backdrop-blur-sm"
          >
            ປະກອບໄດ້
          </Badge>
        ) : stock.allOut ? (
          <Badge
            variant="outline"
            className="absolute top-2 right-2 border-border bg-card/95 text-[10px] shadow-sm backdrop-blur-sm"
          >
            ຫມົດ
          </Badge>
        ) : stock.total > 1 ? (
          <Badge
            variant="secondary"
            className="absolute top-2 right-2 rounded-full text-[10px] shadow-sm"
          >
            {stock.total} ຕົວເລືອກ
          </Badge>
        ) : hasBundle ? (
          <Badge
            variant="outline"
            className="absolute top-2 right-2 border-amber-600/30 bg-amber-500/10 text-[10px] text-amber-900 shadow-sm backdrop-blur-sm"
          >
            ເຊັດ
          </Badge>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-1 p-2.5">
        <p className="line-clamp-2 font-medium text-foreground text-xs leading-snug sm:text-sm">
          {product.name}
        </p>
        <p className="line-clamp-1 text-[10px] text-muted-foreground sm:text-xs">
          {formatProductCategories(product.categories)}
        </p>
        {price ? (
          <p className="mt-auto font-semibold text-secondary text-xs tabular-nums sm:text-sm">
            {formatLakRange(price.min, price.max)}
          </p>
        ) : (
          <p className="mt-auto text-muted-foreground text-xs">ບໍ່ມີຕົວເລືອກ</p>
        )}
      </div>
    </button>
  );
}

function ProductGridPanel({
  isLoading,
  products,
  allowZeroStock,
  enableAssemble,
  searchInput,
  onSearchChange,
  onSelectProduct,
  onCreateProduct,
  createProductLabel = "ສ້າງສິນຄ້າໃໝ່",
  className,
}: {
  isLoading: boolean;
  products: ProductPickerItemDTO[];
  allowZeroStock: boolean;
  enableAssemble: boolean;
  searchInput: string;
  onSearchChange: (value: string) => void;
  onSelectProduct: (product: ProductPickerItemDTO) => void;
  onCreateProduct?: () => void;
  createProductLabel?: string;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-4 py-4", className)}>
      <PickerSearch
        value={searchInput}
        onChange={onSearchChange}
        placeholder="ຄົ້ນຫາຊື່ສິນຄ້າ..."
      />

      {isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {["a", "b", "c", "d", "e", "f"].map((id) => (
            <Skeleton key={id} className="aspect-4/5 rounded-xl" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <Empty className="py-12">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ImageIcon />
            </EmptyMedia>
            <EmptyTitle>ບໍ່ພົບສິນຄ້າ</EmptyTitle>
            <EmptyDescription>
              {onCreateProduct
                ? "ລອງຄົ້ນຫາດ້ວຍຊື່ອື່ນ ຫຼື ສ້າງສິນຄ້າໃໝ່ໃນລະບົບ"
                : "ລອງຄົ້ນຫາດ້ວຍຊື່ອື່ນ ຫຼື ເພີ່ມສິນຄ້າໃນຄລັງກ່ອນ"}
            </EmptyDescription>
          </EmptyHeader>
          {onCreateProduct ? (
            <Button
              type="button"
              variant="outline"
              className="mt-4 rounded-full"
              onClick={onCreateProduct}
            >
              <Plus data-icon="inline-start" />
              {createProductLabel}
            </Button>
          ) : null}
        </Empty>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {products.map((product) => (
            <ProductSelectCard
              key={product.id}
              product={product}
              allowZeroStock={allowZeroStock}
              enableAssemble={enableAssemble}
              onSelect={() => onSelectProduct(product)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function VariantRow({
  variant,
  product,
  onPick,
  allowZeroStock,
  enableAssemble,
  onAssembled,
}: {
  variant: ProductPickerItemDTO["variants"][number];
  product: ProductPickerItemDTO;
  onPick: (picked: PickedVariant) => void;
  allowZeroStock: boolean;
  enableAssemble: boolean;
  onAssembled: (variantId: string, bundleStockQuantity: number) => void;
}) {
  const isBundle = variant.variantType === "bundle";
  const out = !allowZeroStock && variantAvailableQty(variant) <= 0;
  const [isPicking, setIsPicking] = useState(false);
  const [showCheck, setShowCheck] = useState(false);

  const canWriteInventory = useActionPermission("inventory:write");
  const assemblable = useBundleAssemblableQty(
    enableAssemble && isBundle ? variant.id : "",
  );
  const assemble = useAssembleBundle();
  const maxAssemblable = assemblable.data?.maxAssemblableQty ?? 0;
  const canAssemble =
    enableAssemble &&
    isBundle &&
    Boolean(canWriteInventory) &&
    maxAssemblable >= 1 &&
    !assemble.isPending;

  function flashPick() {
    setIsPicking(true);
    setShowCheck(true);
    window.setTimeout(() => setIsPicking(false), 420);
    window.setTimeout(() => setShowCheck(false), 700);
  }

  function handlePick() {
    if (out) return;
    flashPick();
    onPick(buildPickedVariant(variant, product));
  }

  async function handleAssembleAndPick(
    event: MouseEvent<HTMLButtonElement>,
  ) {
    event.preventDefault();
    event.stopPropagation();
    if (!canAssemble) return;
    try {
      const result = await assemble.mutateAsync({
        bundleVariantId: variant.id,
        quantity: 1,
      });
      onAssembled(variant.id, result.bundleStockQuantity);
      flashPick();
      onPick({
        ...buildPickedVariant(variant, product),
        stockQuantity: result.bundleStockQuantity,
      });
      toast.success("ປະກອບ 1 ຊຸດແລ້ວ — ເພີ່ມເຂົ້າອໍເດີ້");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ປະກອບເຊັດລົ້ມເຫຼວ");
    }
  }

  return (
    <li>
      <div
        className={cn(
          "flex min-h-14 w-full items-center gap-2 rounded-xl border px-3 py-3 transition-colors",
          out && !canAssemble
            ? "border-border/60 bg-muted/30 opacity-60"
            : "border-secondary/25 bg-background",
          isPicking && "animate-pick-pop border-secondary/50",
        )}
      >
        <button
          type="button"
          disabled={out}
          onClick={handlePick}
          className={cn(
            "flex min-w-0 flex-1 items-center gap-3 text-left",
            out
              ? "cursor-not-allowed"
              : "hover:opacity-90 active:opacity-80",
          )}
        >
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-medium text-foreground text-sm leading-snug">
                {variant.name}
              </p>
              {isBundle ? (
                <Badge
                  variant="outline"
                  className="border-amber-600/30 bg-amber-500/10 text-[10px] text-amber-900"
                >
                  ເຊັດ
                  {variant.bundleLineCount
                    ? ` · ${variant.bundleLineCount} ລາຍການ`
                    : ""}
                </Badge>
              ) : null}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className="font-semibold text-secondary text-sm tabular-nums">
                {formatLak(variant.price)}
              </span>
              {out ? (
                <Badge
                  variant="outline"
                  className="border-border bg-background text-[10px] text-foreground"
                >
                  ຫມົດ
                </Badge>
              ) : (
                <>
                  <span className="text-muted-foreground text-xs tabular-nums">
                    {formatQtyWithUnit(
                      variant.stockQuantity,
                      variant.stockUnitLabel,
                    )}{" "}
                    ຄົງ
                  </span>
                  {(variant.draftReservedQuantity ?? 0) > 0 ? (
                    <>
                      <span className="text-amber-800 text-xs tabular-nums">
                        ·{" "}
                        {formatQtyWithUnit(
                          variant.draftReservedQuantity!,
                          variant.stockUnitLabel,
                        )}{" "}
                        ຮ່າງ
                      </span>
                      <span className="font-medium text-emerald-800 text-xs tabular-nums">
                        ·{" "}
                        {formatQtyWithUnit(
                          variantAvailableQty(variant),
                          variant.stockUnitLabel,
                        )}{" "}
                        ພ້ອມຂາຍ
                      </span>
                    </>
                  ) : null}
                </>
              )}
              {isBundle && enableAssemble && maxAssemblable > 0 ? (
                <span className="text-muted-foreground text-xs tabular-nums">
                  · ປະກອບໄດ້ {maxAssemblable} ຊຸດ
                </span>
              ) : null}
            </div>
          </div>
          {!out ? (
            <span
              className={cn(
                "flex size-10 shrink-0 items-center justify-center rounded-full bg-secondary/15 text-secondary",
                showCheck && "bg-secondary text-secondary-foreground",
              )}
            >
              {showCheck ? (
                <CheckIcon className="animate-pick-check" />
              ) : (
                <Plus />
              )}
            </span>
          ) : null}
        </button>

        {enableAssemble && isBundle && canWriteInventory ? (
          <Button
            type="button"
            size="sm"
            variant={out ? "default" : "outline"}
            className="shrink-0 rounded-full"
            disabled={!canAssemble}
            isLoading={assemble.isPending || assemblable.isLoading}
            onClick={handleAssembleAndPick}
            title={
              maxAssemblable < 1
                ? "ສ່ວນປະກອບບໍ່ພໍສຳລັບປະກອບ"
                : "ປະກອບ 1 ຊຸດ ແລ້ວເພີ່ມເຂົ້າອໍເດີ້"
            }
          >
            <Package data-icon="inline-start" />
            {out ? "ປະກອບແລ້ວເພີ່ມ" : "ປະກອບ"}
          </Button>
        ) : null}
      </div>
    </li>
  );
}

function VariantPickerPanel({
  product,
  allowZeroStock,
  variantSearch,
  onVariantSearchChange,
  onBack,
  onPick,
  enableAssemble,
  onAssembled,
  onCreateVariant,
  createVariantLabel = "ສ້າງ variant ໃໝ່",
  className,
}: {
  product: ProductPickerItemDTO;
  allowZeroStock: boolean;
  variantSearch: string;
  onVariantSearchChange: (value: string) => void;
  onBack: () => void;
  onPick: (picked: PickedVariant) => void;
  enableAssemble: boolean;
  onAssembled: (variantId: string, bundleStockQuantity: number) => void;
  onCreateVariant?: () => void;
  createVariantLabel?: string;
  className?: string;
}) {
  const filteredVariants = useMemo(
    () =>
      product.variants.filter((variant) =>
        matchesSearch(variant.name, variantSearch),
      ),
    [product.variants, variantSearch],
  );

  return (
    <div className={cn("flex flex-col gap-4 py-4", className)}>
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-10 shrink-0 rounded-full"
          onClick={onBack}
          aria-label="ກັບໄປລາຍການສິນຄ້າ"
        >
          <ArrowLeftIcon />
        </Button>
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="size-12 shrink-0 overflow-hidden rounded-lg bg-muted/40">
            {product.imageKey ? (
              <AppImage
                src={product.imageKey}
                alt={product.name}
                className="size-full"
                fit="cover"
              />
            ) : (
              <div className="flex size-full items-center justify-center text-muted-foreground">
                <ImageIcon className="opacity-40" />
              </div>
            )}
          </div>
          <div className="min-w-0">
            <p className="line-clamp-2 font-medium text-foreground text-sm leading-snug">
              {product.name}
            </p>
            <p className="text-muted-foreground text-xs">
              {product.variants.length} ຕົວເລືອກ ·{" "}
              {formatProductCategories(product.categories)}
            </p>
          </div>
        </div>
      </div>

      <PickerSearch
        value={variantSearch}
        onChange={onVariantSearchChange}
        placeholder="ຄົ້ນຫາຕົວເລືອກ..."
        autoFocus
      />

      {filteredVariants.length === 0 ? (
        <Empty className="py-10">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <SearchIcon />
            </EmptyMedia>
            <EmptyTitle>ບໍ່ພົບຕົວເລືອກ</EmptyTitle>
            <EmptyDescription>
              {onCreateVariant
                ? "ລອງຄົ້ນຫາດ້ວຍຊື່ອື່ນ ຫຼື ສ້າງ variant ໃໝ່ໃນສິນຄ້ານີ້"
                : "ລອງຄົ້ນຫາດ້ວຍຊື່ອື່ນ"}
            </EmptyDescription>
          </EmptyHeader>
          {onCreateVariant ? (
            <Button
              type="button"
              variant="outline"
              className="mt-4 rounded-full"
              onClick={onCreateVariant}
            >
              <Plus data-icon="inline-start" />
              {createVariantLabel}
            </Button>
          ) : null}
        </Empty>
      ) : (
        <>
          <ul className="flex flex-col gap-2">
            {filteredVariants.map((variant) => (
              <VariantRow
                key={variant.id}
                variant={variant}
                product={product}
                onPick={onPick}
                allowZeroStock={allowZeroStock}
                enableAssemble={enableAssemble}
                onAssembled={onAssembled}
              />
            ))}
          </ul>
          {onCreateVariant ? (
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              onClick={onCreateVariant}
            >
              <Plus data-icon="inline-start" />
              {createVariantLabel}
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}

function PickerTitle({
  step,
  subtitle,
}: {
  step: PickerStep;
  subtitle: string;
}) {
  return (
    <>
      <span className="font-display text-lg sm:text-xl">
        {step === "products" ? "ເລືອກສິນຄ້າ" : "ເລືອກຕົວເລືອກ"}
      </span>
      <span className="text-muted-foreground text-sm">
        {step === "products" ? subtitle : "ຄົ້ນຫາ ແລະ ກົດເພື່ອເພີ່ມເຂົ້າອໍເດີ້"}
      </span>
    </>
  );
}

function ProductPickerShell({
  header,
  body,
  footer,
}: {
  header: ReactNode;
  body: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0">{header}</div>
      <Separator />
      <div className="min-h-0 flex-1 overflow-hidden">
        <ScrollArea className="h-full">{body}</ScrollArea>
      </div>
      {footer ? (
        <>
          <Separator />
          <div className="shrink-0">{footer}</div>
        </>
      ) : null}
    </div>
  );
}

const PICKER_SHELL_CLASS =
  "flex h-[96dvh] max-h-[96dvh] flex-col gap-0 overflow-hidden p-0";

export function ProductPickerModal({
  open,
  onOpenChange,
  onPick,
  allowZeroStock = false,
  purpose = "order",
  subtitle = "ກົດກາດສິນຄ້າ ແລ້ວເລືອກຕົວເລືອກ",
  excludeOrderId,
  localReservedByVariant,
  excludeBundles = false,
  onCreateProduct,
  createProductLabel = "ສ້າງສິນຄ້າໃໝ່",
  onCreateVariant,
  createVariantLabel = "ສ້າງ variant ໃໝ່",
}: ProductPickerModalProps) {
  const isMobile = useIsMobile();
  const [step, setStep] = useState<PickerStep>("products");
  const [selectedProduct, setSelectedProduct] =
    useState<ProductPickerItemDTO | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [variantSearch, setVariantSearch] = useState("");

  const debounced = useDebounceCallback((val: string) => setSearch(val), 350);

  const picker = useProductPickerQuery({
    search: search || undefined,
    purpose,
    excludeOrderId: purpose === "order" ? excludeOrderId : undefined,
  });
  const products = useMemo(() => {
    const rows = applyLocalReservedToProducts(
      picker.data?.data ?? [],
      purpose === "order" ? localReservedByVariant : undefined,
    );
    return filterPickerProducts(rows, { excludeBundles });
  }, [picker.data?.data, excludeBundles, localReservedByVariant, purpose]);

  const activeProduct = useMemo(() => {
    if (!selectedProduct) return null;
    return products.find((product) => product.id === selectedProduct.id) ?? null;
  }, [products, selectedProduct]);

  useEffect(() => {
    if (!open) {
      setStep("products");
      setSelectedProduct(null);
      setSearchInput("");
      setSearch("");
      setVariantSearch("");
    }
  }, [open]);

  function handleSearchChange(value: string) {
    setSearchInput(value);
    debounced(value);
  }

  function handleSelectProduct(product: ProductPickerItemDTO) {
    setSelectedProduct(product);
    setVariantSearch("");
    setStep("variants");
  }

  function handleBack() {
    setStep("products");
    setSelectedProduct(null);
    setVariantSearch("");
  }

  function handleAssembled(variantId: string, bundleStockQuantity: number) {
    setSelectedProduct((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        variants: prev.variants.map((variant) =>
          variant.id === variantId
            ? {
                ...variant,
                stockQuantity: bundleStockQuantity,
                availableQuantity: bundleStockQuantity,
                draftReservedQuantity: 0,
              }
            : variant,
        ),
      };
    });
  }

  const contentPadding = isMobile ? "px-4" : "px-6";
  const enableAssemble = purpose === "order" && !excludeBundles;

  function handleCreateProduct() {
    onOpenChange(false);
    onCreateProduct?.();
  }

  const createProductFooter =
    onCreateProduct && step === "products" ? (
      <div className={cn("py-3", contentPadding)}>
        <Button
          type="button"
          variant="outline"
          className="w-full rounded-full sm:w-auto"
          onClick={handleCreateProduct}
        >
          <Plus data-icon="inline-start" />
          {createProductLabel}
        </Button>
      </div>
    ) : null;

  const body =
    step === "products" ? (
      <ProductGridPanel
        isLoading={picker.isLoading}
        products={products}
        allowZeroStock={allowZeroStock}
        enableAssemble={enableAssemble}
        searchInput={searchInput}
        onSearchChange={handleSearchChange}
        onSelectProduct={handleSelectProduct}
        onCreateProduct={onCreateProduct ? handleCreateProduct : undefined}
        createProductLabel={createProductLabel}
        className={contentPadding}
      />
    ) : activeProduct ? (
      <VariantPickerPanel
        product={activeProduct}
        allowZeroStock={allowZeroStock}
        variantSearch={variantSearch}
        onVariantSearchChange={setVariantSearch}
        onBack={handleBack}
        onPick={onPick}
        enableAssemble={enableAssemble}
        onAssembled={handleAssembled}
        onCreateVariant={
          onCreateVariant ? () => onCreateVariant(activeProduct) : undefined
        }
        createVariantLabel={createVariantLabel}
        className={contentPadding}
      />
    ) : null;

  const headerTitle = <PickerTitle step={step} subtitle={subtitle} />;

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className={cn(PICKER_SHELL_CLASS, "px-0 pb-4")}>
          <ProductPickerShell
            header={
              <DrawerHeader className="gap-1 px-4 pt-1 pb-3 text-left">
                <DrawerTitle className="flex flex-col gap-0.5">
                  {headerTitle}
                </DrawerTitle>
                <DrawerDescription className="sr-only">
                  ເລືອກສິນຄ້າ ແລະ ຕົວເລືອກເພື່ອເພີ່ມໃນອໍເດີ້
                </DrawerDescription>
              </DrawerHeader>
            }
            body={body}
            footer={createProductFooter}
          />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(PICKER_SHELL_CLASS, "!max-w-5xl w-[96vw]")}
        showCloseButton
      >
        <ProductPickerShell
          header={
            <DialogHeader className="gap-1 px-6 pt-6 pb-4 text-left">
              <DialogTitle className="flex flex-col gap-0.5 pr-8">
                {headerTitle}
              </DialogTitle>
              <DialogDescription className="sr-only">
                ເລືອກສິນຄ້າ ແລະ ຕົວເລືອກເພື່ອເພີ່ມໃນອໍເດີ້
              </DialogDescription>
            </DialogHeader>
          }
          body={body}
          footer={createProductFooter}
        />
      </DialogContent>
    </Dialog>
  );
}
